import { useAppContext } from '@common/contexts/AppContext';
import { getAvailableRoomById, roomsKeys } from '@features/rooms';
import { moreRoomsAvailableThanInBasket } from '@features/rates/helpers/moreRoomsAvailableThanInBasket.helper';
import { useQueries } from '@tanstack/react-query';
import { toCamelCase } from 'case-converter';
import { basketSelectors } from '@store/basket/selectors';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Item } from 'shared-types';
import { BasketDetails } from 'shared-types/Basket';
import { RoomUpgrade } from 'shared-types/Rate';
import { hasExtras, roomExtrasQuery } from './roomExtrasOffered';
import {
  isUpgradeableRoomItem,
  roomIdsInBasket,
  toRoomExtrasParams,
  toUpgradeAvailabilityParams,
} from './roomUpgradeItems';
import { upgradedRoomIds } from './upgradedRooms';

const UPGRADE_STALE_TIME_MS = 60_000;

export type BasketRoomUpgradeOffer = {
  item: Item;
  upgrade: RoomUpgrade;
};

type AvailableRoom = Awaited<ReturnType<typeof getAvailableRoomById>>;

export const upgradeAvailabilityQuery = (item: Item, siteId: number) => {
  const { rateId: _rateId, ...params } = toUpgradeAvailabilityParams(item);
  return {
    queryKey: roomsKeys.availableRoomById({ siteId, isMultiRoomsEnabled: false, ...params }),
    queryFn: () => getAvailableRoomById({ siteId, isMultiRoomsEnabled: false, ...params }),
  };
};

export const findRoomUpgrade = (
  item: Item,
  availableRoom: AvailableRoom | undefined,
  basketRoomIds: string[]
): RoomUpgrade | undefined => {
  const { rateId } = toUpgradeAvailabilityParams(item);
  const rates = availableRoom?.rates;
  const rate = Array.isArray(rates) ? rates.find(({ id }) => id === rateId) : undefined;
  if (!rate?.room_upgrade) return undefined;

  const upgrade = toCamelCase(rate.room_upgrade) as RoomUpgrade;
  return moreRoomsAvailableThanInBasket(upgrade, basketRoomIds) ? upgrade : undefined;
};

export const useBasketRoomUpgradeOffers = (items: Item[], enabled: boolean) => {
  const { siteId } = useAppContext();
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as BasketDetails | undefined;
  const basketId = basketDetails?.id;

  const [expiryTick, setExpiryTick] = useState(0);

  const roomItems = useMemo(() => {
    if (!enabled) return [];
    const upgraded = upgradedRoomIds(basketId);
    return items.filter(
      (item) => isUpgradeableRoomItem(item, items) && !upgraded.has(String(item.id))
    );
  }, [basketId, enabled, items, expiryTick]);

  useEffect(() => {
    const now = Date.now();
    const nextExpiry = Math.min(
      ...roomItems.map((item) => Date.parse(item.expireAt ?? '')).filter((at) => at > now)
    );
    if (!Number.isFinite(nextExpiry)) return undefined;
    const timer = setTimeout(() => setExpiryTick((tick) => tick + 1), nextExpiry - now + 1);
    return () => clearTimeout(timer);
  }, [roomItems]);

  const queries = useQueries({
    queries: roomItems.map((item) => ({
      ...upgradeAvailabilityQuery(item, siteId),
      staleTime: UPGRADE_STALE_TIME_MS,
    })),
  });

  const extrasQueries = useQueries({
    queries: roomItems.map((item) => roomExtrasQuery(siteId, toRoomExtrasParams(item))),
  });

  const basketRoomIds = useMemo(() => roomIdsInBasket(items), [items]);

  return roomItems.reduce<BasketRoomUpgradeOffer[]>((offers, item, index) => {
    const extras = extrasQueries[index];
    if (!extras?.isSuccess || hasExtras(extras.data)) return offers;
    const upgrade = findRoomUpgrade(item, queries[index]?.data, basketRoomIds);
    return upgrade ? [...offers, { item, upgrade }] : offers;
  }, []);
};
